import type { FindingOut } from '@/api/types';

export interface GlAccountRollupRow {
  glAccount: string;
  findingCount: number;
  pendingCount: number;
  /** Sum of effective recovery (practitioner override wins). Single-
   *  currency assumption — same as the run page's other rollups. */
  totalRecovery: number;
}

function evidenceString(finding: FindingOut, key: string): string | null {
  const value = (finding.evidence as Record<string, unknown>)[key];
  if (typeof value !== 'string') return null;
  const s = value.trim();
  return s || null;
}

/** Per-GL-account findings rollup for a run, largest recovery first.
 *  Findings without a GL account in evidence are grouped under null
 *  and dropped — they can't deep-link to the workbench. */
export function rollupFindingsByGlAccount(findings: FindingOut[]): GlAccountRollupRow[] {
  const byAccount = new Map<string, GlAccountRollupRow>();
  for (const finding of findings) {
    const glAccount = evidenceString(finding, 'gl_account.name');
    if (!glAccount) continue;
    let row = byAccount.get(glAccount);
    if (!row) {
      row = { glAccount, findingCount: 0, pendingCount: 0, totalRecovery: 0 };
      byAccount.set(glAccount, row);
    }
    row.findingCount += 1;
    if (finding.status === 'pending' || finding.status === 'needs_review') {
      row.pendingCount += 1;
    }
    row.totalRecovery += Number(finding.recovery.amount) || 0;
  }
  return [...byAccount.values()].sort(
    (a, b) => b.totalRecovery - a.totalRecovery || a.glAccount.localeCompare(b.glAccount),
  );
}
